import React, { useState } from 'react';
import { doctorInfo } from '../data/mock';
import { Mail, Phone, MapPin, Send, CheckCircle, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    consent: false
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', phone: '', message: '', consent: false });
    }, 3000);
  };

  return (
    <section id="contact" className="section-padding bg-white">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <div>
            <p className="text-sm uppercase tracking-widest text-[#b8860b] font-medium mb-4">
              Get in Touch
            </p>
            <h2 className="heading-1 text-[#0f172a] mb-6">
              Request a Consultation
            </h2>
            <p className="body-large text-[#475569] mb-10">
              When your health deserves nothing less than clarity, care, and excellence — 
              Dr. Castellon is the doctor to call.
            </p>

            {/* Contact Details */}
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#0f172a] flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-[#0f172a] mb-1">
                    Location
                  </h4>
                  <p className="text-sm text-[#475569]">
                    {doctorInfo.location}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#0f172a] flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-[#0f172a] mb-1">
                    Email
                  </h4>
                  <a href={`mailto:${doctorInfo.email}`} className="text-sm text-[#475569] hover:text-[#b8860b] transition-colors">
                    {doctorInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#25d366] flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-[#0f172a] mb-1">
                    WhatsApp
                  </h4>
                  <a href={`https://wa.me/${doctorInfo.phone.replace(/[\s+]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#475569] hover:text-[#25d366] transition-colors" data-testid="whatsapp-link">
                    {doctorInfo.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-[#f8fafc] p-8 lg:p-10 border border-[#e2e8f0]">
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-16">
                <div className="w-16 h-16 bg-[#0f172a] flex items-center justify-center mb-6">
                  <CheckCircle size={32} className="text-white" />
                </div>
                <h3 className="heading-3 text-[#0f172a] mb-2">
                  Message Received
                </h3>
                <p className="text-sm text-[#475569]">
                  We will respond to your inquiry shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="text-center mb-6">
                  <p className="text-sm text-[#64748b]">
                    Send us a message or reach out via WhatsApp
                  </p>
                </div>
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-[#0f172a] mb-2 block">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="border-[#e2e8f0] bg-white focus:border-[#0f172a] rounded-none"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-[#0f172a] mb-2 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="border-[#e2e8f0] bg-white focus:border-[#0f172a] rounded-none"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-[#0f172a] mb-2 block">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="border-[#e2e8f0] bg-white focus:border-[#0f172a] rounded-none"
                    placeholder="+34 ..."
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-medium text-[#0f172a] mb-2 block">
                    How can we help? *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="border-[#e2e8f0] bg-white focus:border-[#0f172a] rounded-none resize-none"
                    placeholder="Please describe your inquiry..."
                  />
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="consent"
                    checked={formData.consent}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent: checked }))}
                    className="mt-1 rounded-none border-[#e2e8f0] data-[state=checked]:bg-[#0f172a]"
                  />
                  <Label htmlFor="consent" className="text-xs text-[#64748b] leading-relaxed cursor-pointer">
                    I consent to my personal data being processed in accordance with the Privacy Policy. 
                    I understand this does not establish a physician-patient relationship.
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={!formData.consent}
                  className="w-full bg-[#0f172a] hover:bg-[#b8860b] text-white py-4 rounded-none font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send size={16} className="mr-2" />
                  Send Message
                </Button>

                <p className="text-xs text-[#94a3b8] text-center">
                  For medical emergencies, please contact local emergency services immediately.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
